import { Components, hooks } from "botframework-webchat-component";
import { ClipboardEvent, DragEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { hide } from "components/Home/homeVisibilitySlice";
import { Welcome } from "components/Home/Welcome";
import SkillChips from "components/skills/SkillChips";
import SkillSlashMenu from "components/skills/SkillSlashMenu";
import {
  clearPendingSkill,
  removeActiveSkill,
  selectSkill,
  SkillInfo,
} from "components/skills/skillsSlice";
import { queueDropSkill, setPendingSkill } from "components/skills/skillsState";
import { setTextToSearch } from "shared/components/Cards/Card/cardTextSlice";
import { CardList } from "shared/components/Cards/CardList";
import { userSendPrompt } from "shared/components/SideNav/userSendPromptSlicer";
import { toggleSideNav } from "shared/components/SideNav/SideNavToggleButton/toggleSideNav";


import { showRefreshButton, hideRefreshButton } from "shared/components/SideNav/refreshButtonUtils";
import {
  ACCEPTED_FILE_ACCEPT,
  AttachedFile,
  buildMessageWithAttachments,
  extractTextFromFile,
  isAcceptedFile,
  shouldAttachPastedText,
} from "shared/functions/fileToText";
import PromptButton from "../PromptButton/PromptButton";

import "./webChat.css";

const DISCONNECTED_STATES = ['error', 'notconnected', 'sagaerror'];

const AttachIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.5 7.5L8.2 12.8C6.9 14.1 4.8 14.1 3.5 12.8C2.2 11.5 2.2 9.4 3.5 8.1L9.2 2.4C10.1 1.5 11.5 1.5 12.4 2.4C13.3 3.3 13.3 4.7 12.4 5.6L6.9 11.1C6.5 11.5 5.8 11.5 5.4 11.1C5 10.7 5 10 5.4 9.6L10.3 4.7"
      stroke="#0074D4"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const { useSendMessage } = hooks;
function WebChat(props: { cardText: string; homeVisibility: boolean }) {
  const [userMessage, setUserMessage] = useState<string>();
  const [reconnectStatus, setReconnectStatus] = useState<'idle' | 'reconnecting' | 'failed'>('idle');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isReadingFiles, setIsReadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Menú "/" de skills: opción resaltada y si el usuario lo cerró con
  // Esc (se vuelve a abrir cuando el texto deja de empezar con "/").
  const [skillMenuIndex, setSkillMenuIndex] = useState(0);
  const [skillMenuDismissed, setSkillMenuDismissed] = useState(false);
  const skills = useSelector((state: any) => state.skills);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noResponseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dispatch = useDispatch();
  const sendMessage = useSendMessage();
  const { useConnectivityStatus, useActivities } = hooks as any;
  const [connectivityStatus] = useConnectivityStatus();
  const [activities] = useActivities();

  useEffect(() => {
    if (props.cardText !== "") {
      setUserMessage(props.cardText);
    }
  });

  // Clear reconnect overlay once connection is restored
  useEffect(() => {
    if (connectivityStatus === 'connected' || connectivityStatus === 'reconnected') {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      setReconnectStatus('idle');
      hideRefreshButton();
    }

    // Show overlay automatically when disconnected, without waiting for user to send
    if (DISCONNECTED_STATES.includes(connectivityStatus) || connectivityStatus === 'connectingslow') {
      if (reconnectStatus === 'idle') {
        setReconnectStatus('reconnecting');
        reconnectTimerRef.current = setTimeout(() => {
          setReconnectStatus('failed');
        }, 5000);
      }
    }
  }, [connectivityStatus]);

  // Mostrar/ocultar y parpadear el botón Refresh según el estado de reconexión
  useEffect(() => {
    if (reconnectStatus === 'failed') {
      showRefreshButton(true);
    } else {
      hideRefreshButton();
    }
  }, [reconnectStatus]);

  // Cancel the no-response timer as soon as bot starts typing
  useEffect(() => {
    if (!activities?.length) return;
    const last = activities[activities.length - 1];
    if (last?.type === 'typing') {
      if (noResponseTimerRef.current) {
        clearTimeout(noResponseTimerRef.current);
        noResponseTimerRef.current = null;
      }
      setReconnectStatus('idle');
    }
  }, [activities]);

  const attemptReconnect = useCallback(() => {
    setReconnectStatus('reconnecting');
    reconnectTimerRef.current = setTimeout(() => {
      window.location.reload();
    }, 2000);
  }, []);

  // ---- Skills: menú "/" y chips azules ------------------------------
  // El menú se abre mientras TODO el texto es "/" + un filtro sin
  // espacios ("/", "/new", "/new-skill"); al elegir una skill, el texto
  // se limpia y la skill queda como chip azul hasta que el bot la suelte.
  const skillQuery = useMemo(() => {
    const match = /^\/(\S*)$/.exec(userMessage ?? "");
    return match ? match[1].toLowerCase() : null;
  }, [userMessage]);

  const skillMenuItems: SkillInfo[] = useMemo(() => {
    if (skillQuery === null) return [];
    return (skills.catalog as SkillInfo[]).filter(
      (skill) =>
        skill.command.toLowerCase().slice(1).includes(skillQuery) ||
        skill.name.includes(skillQuery) ||
        skill.title.toLowerCase().includes(skillQuery)
    );
  }, [skills.catalog, skillQuery]);

  // Sin skills en el bot (ENABLE_SKILLS apagado) el menú no se abre: un
  // texto que empieza con "/" se manda como cualquier otro.
  const skillMenuOpen =
    skillQuery !== null &&
    !skillMenuDismissed &&
    (!skills.catalogLoaded || skills.catalog.length > 0);

  useEffect(() => {
    setSkillMenuIndex(0);
    if (skillQuery === null) setSkillMenuDismissed(false);
  }, [skillQuery]);

  const activeSkillInfos: SkillInfo[] = useMemo(
    () =>
      (skills.active as string[]).map(
        (name) =>
          (skills.catalog as SkillInfo[]).find((skill) => skill.name === name) || {
            name,
            command: `/${name}`,
            title: name,
            summary: "",
            commandOnly: false,
          }
      ),
    [skills.active, skills.catalog]
  );
  const skillMode = Boolean(skills.pending) || activeSkillInfos.length > 0;

  const chooseSkill = useCallback(
    (skill: SkillInfo) => {
      dispatch(selectSkill(skill));
      setPendingSkill(skill.name);
      setUserMessage("");
      setSkillMenuDismissed(false);
      textareaRef.current?.focus();
    },
    [dispatch]
  );

  const removeSkill = useCallback(
    (skill: SkillInfo, wasActive: boolean) => {
      if (wasActive) {
        // El bot la apaga al empezar el siguiente turno.
        queueDropSkill(skill.name);
        dispatch(removeActiveSkill(skill.name));
      } else {
        setPendingSkill(null);
        dispatch(clearPendingSkill());
      }
      textareaRef.current?.focus();
    },
    [dispatch]
  );

  const handleKeyDown = (event: any) => {
    let keyboardEvent = event as KeyboardEvent;
    if (skillMenuOpen) {
      if (keyboardEvent.key === "Escape") {
        keyboardEvent.preventDefault();
        setSkillMenuDismissed(true);
        return;
      }
      if (skillMenuItems.length > 0) {
        if (keyboardEvent.key === "ArrowDown" || keyboardEvent.key === "ArrowUp") {
          keyboardEvent.preventDefault();
          const step = keyboardEvent.key === "ArrowDown" ? 1 : -1;
          setSkillMenuIndex(
            (prev) => (prev + step + skillMenuItems.length) % skillMenuItems.length
          );
          return;
        }
        if (
          (keyboardEvent.key === "Enter" && !keyboardEvent.shiftKey) ||
          keyboardEvent.key === "Tab"
        ) {
          keyboardEvent.preventDefault();
          chooseSkill(skillMenuItems[Math.min(skillMenuIndex, skillMenuItems.length - 1)]);
          return;
        }
      }
    }
    if (keyboardEvent.key === "Enter" && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      sendMessageToBot();
    }
  };

  // Convierte cada archivo (PDF / HTML / Markdown / TXT) a texto plano y lo deja como
  // chip adjunto; el contenido se anexa al mensaje al enviarlo.
  const addFiles = useCallback(async (incoming: FileList | File[]) => {
    const files = Array.from(incoming);
    const rejected = files.filter((file) => !isAcceptedFile(file));
    setAttachmentError(
      rejected.length
        ? `Only PDF, HTML, Markdown and TXT files are supported: ${rejected.map((file) => file.name).join(", ")}`
        : null
    );
    const accepted = files.filter(isAcceptedFile);
    if (!accepted.length) return;
    setIsReadingFiles(true);
    try {
      for (const file of accepted) {
        const text = await extractTextFromFile(file);
        setAttachedFiles((prev) =>
          prev.some((attached) => attached.name === file.name)
            ? prev.map((attached) =>
                attached.name === file.name
                  ? { name: file.name, text, kind: 'file' as const }
                  : attached
              )
            : [...prev, { name: file.name, text, kind: 'file' as const }]
        );
      }
    } catch (error) {
      console.error("Could not read attached file:", error);
      setAttachmentError(
        error instanceof Error ? error.message : "Could not read the attached file"
      );
    } finally {
      setIsReadingFiles(false);
    }
  }, []);

  const removeAttachedFile = useCallback((name: string) => {
    setAttachedFiles((prev) => prev.filter((file) => file.name !== name));
  }, []);

  // Texto largo pegado o arrastrado: se vuelve adjunto tipo "paste",
  // separado del input normal (el backend no le aplica sinónimos).
  const addPastedText = useCallback((text: string) => {
    setAttachedFiles((prev) => {
      let index = 1;
      while (prev.some((file) => file.name === `Pasted text ${index}`)) {
        index++;
      }
      return [...prev, { name: `Pasted text ${index}`, text, kind: 'paste' as const }];
    });
    setAttachmentError(null);
  }, []);

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboard = event.clipboardData;
    if (clipboard.files?.length) {
      event.preventDefault();
      addFiles(clipboard.files);
      return;
    }
    const text = clipboard.getData('text');
    // Un prompt corto pegado es input normal; sólo el texto largo
    // (> 120 palabras) se vuelve adjunto.
    if (shouldAttachPastedText(text)) {
      event.preventDefault();
      addPastedText(text);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    const types = Array.from(event.dataTransfer.types);
    if (types.includes("Files") || types.includes("text/plain")) {
      event.preventDefault();
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    if (event.dataTransfer.files?.length) {
      addFiles(event.dataTransfer.files);
      return;
    }
    const text = event.dataTransfer.getData('text');
    if (shouldAttachPastedText(text)) {
      addPastedText(text);
    } else if (text) {
      setUserMessage((prev) => (prev ? `${prev}${text}` : text));
    }
  };

  const handleSubmit = () => {
    sendMessageToBot();
  };

  const sendMessageToBot = () => {
    if (DISCONNECTED_STATES.includes(connectivityStatus)) {
      attemptReconnect();
      return;
    }
    // Con una skill recién elegida se puede enviar sin texto: el
    // mensaje es su comando (p. ej. "/new-skill") y la skill arranca.
    const messageText = userMessage?.trim() ? userMessage : skills.pending?.command;
    if (messageText?.trim()) {
      // El texto de los adjuntos viaja dentro del mismo mensaje
      const outgoingMessage = buildMessageWithAttachments(messageText, attachedFiles);

      sendMessage(outgoingMessage);
      // Start 5-second timer — if no typing indicator appears, bot is unreachable
      if (noResponseTimerRef.current) clearTimeout(noResponseTimerRef.current);
      noResponseTimerRef.current = setTimeout(() => {
        setReconnectStatus('failed');
      }, 5000);

      setUserMessage("");
      setAttachedFiles([]);
      setAttachmentError(null);
      dispatch(setTextToSearch(""));
      dispatch(hide());
      dispatch(userSendPrompt(messageText));
      dispatch(toggleSideNav(false));
    }
  };

  return (
    <>
      <Components.AccessKeySinkSurface>
        <div
          className={props.homeVisibility ? 'webchat-content-home' : 'webchat-content'}
        >

          <div className="chat-promp-area">
            <Components.BasicToaster />
            {props.homeVisibility && (
              <>
                <div className="chat-welcome">
                  <Welcome />
                  <CardList />
                </div>
              </>
            )}
            {!props.homeVisibility && <Components.BasicTranscript />}
          </div>
          <div
            className={isDraggingFile ? 'chat-sendbox chat-sendbox--dragover' : 'chat-sendbox'}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <PromptButton cardText={props.cardText} reconnectFailed={reconnectStatus === 'failed'} />
            <Components.BasicSendBox className="content" />
            {(attachedFiles.length > 0 || isReadingFiles || attachmentError) && (
              <div className="attached-files-row">
                {attachedFiles.map((file) => (
                  <span
                    key={file.name}
                    className={
                      file.kind === 'paste'
                        ? 'attached-file-chip attached-file-chip--paste'
                        : 'attached-file-chip'
                    }
                  >
                    <span className="attached-file-name" title={file.name}>
                      {file.kind === 'paste'
                        ? `${file.name} · ${
                            file.text.length < 1000
                              ? `${file.text.length} chars`
                              : `${(file.text.length / 1000).toFixed(1)}K chars`
                          }`
                        : file.name}
                    </span>
                    <button
                      type="button"
                      className="attached-file-remove"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeAttachedFile(file.name)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {isReadingFiles && (
                  <span className="attached-file-chip attached-file-chip--loading">Reading file...</span>
                )}
                {attachmentError && <span className="attached-files-error">{attachmentError}</span>}
              </div>
            )}
            <SkillChips
              pending={skills.pending}
              active={activeSkillInfos}
              onRemove={removeSkill}
            />
            <div className="textarea-wrapper">
              {skillMenuOpen && (
                <SkillSlashMenu
                  items={skillMenuItems}
                  highlighted={skillMenuIndex}
                  loaded={skills.catalogLoaded}
                  onSelect={chooseSkill}
                  onHighlight={setSkillMenuIndex}
                />
              )}
              <textarea
                id="sendbox-textarea"
                ref={textareaRef}
                className={skillMode ? 'sendbox-textarea--skill' : undefined}
                placeholder={
                  skills.pending
                    ? `Describe what you need for ${skills.pending.title}, or press Enter to start`
                    : skillMode
                      ? 'Continue with the active skill, or type your own question'
                      : 'Select your default prompt or type your own question'
                }
                value={userMessage}
                onChange={(event) => setUserMessage(event.target.value)}
                rows={4}
                cols={50}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={reconnectStatus !== 'idle'}
              ></textarea>

              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_ACCEPT}
                multiple
                style={{ display: 'none' }}
                onChange={(event) => {
                  if (event.target.files?.length) {
                    addFiles(event.target.files);
                  }
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                id="attach-file-button"
                title="Attach a PDF, HTML or Markdown file (or drag & drop it here)"
                onClick={() => fileInputRef.current?.click()}
                disabled={reconnectStatus !== 'idle' || isReadingFiles}
              >
                <AttachIcon />
              </button>
              <button onClick={handleSubmit} id="send-message-button" disabled={reconnectStatus !== 'idle'}></button>
            </div>

            <div className="connectivity-status-row">
              <Components.BasicConnectivityStatus />
            </div>
            <div className="policy-content">
              <span className="policy-text">
                Always remember to use LUCA Bot in a responsable manner. For
                more information, please review our{" "}
                <a
                  target="_blank"
                  href="https://cemex.sharepoint.com/sites/ProcessIT/SitePages/Artificial-Intelligence-Policy.aspx"
                  className="policy-link"
                >
                  Cemex AI policy
                </a>
              </span>
            </div>
          </div>
        </div>
      </Components.AccessKeySinkSurface>
    </>
  );
}

export default memo(WebChat);
